import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { UsersOrderBy, Role } from '@/interfaces/user'
import { showErrors, showNotification } from '@/utils/snack'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppRefreshAndExport from '@/components/AppRefreshAndExport.vue'
import AppSearchBtn from '@/components/AppSearchBtn.vue'
import AppSimpleTextField from '@/components/AppSimpleTextField.vue'
import ListPage from '@/mixins/list-page'
import ManagerDialog from './script-dialog'
import { exportManagersList, findManagers } from './gql'
import { TypedGqlError } from '@/interfaces/error'
import { SnackType } from '@/interfaces/snack'
import { downloadBase64 } from '@/helpers/base64-to-file'

@Component({
  components: {
    AppBreadcrumbs,
    AppCheckbox,
    AppRefreshAndExport,
    AppDialog,
    AppSimpleTextField,
    AppSearchBtn,
    AppTextField
  }
})

export default class ManagerList extends mixins(ManagerDialog, ListPage) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: '系统管理'
    }, {
      text: '管理员列表'
    }
  ]

  options = {
    sortBy: ['createdAt'],
    page: 1,
    itemsPerPage: 10,
    sortDesc: [true]
  }

  filter = {
    username: ''
  }

  totalNum = 0
  usersLoading = true

  headers = [
    { text: '管理员姓名', value: 'username' },
    { text: '电话', value: 'phone' },
    { text: '管理员类别', value: 'role', sortable: false },
    { text: '操作', value: 'actions', sortable: false, align: 'end' }
  ]

  users = []

  windowSize = {
    w: 0,
    h: 0
  }

  actionsHeight = 51

  @State(state => state.page.UserList) cache!: string

  mounted () {
    this.onResize()
    this.restorePage(this.cache)
  }

  @Watch('options', { deep: true })
  onOptionsChanged () {
    this.getManagers()
  }

  canAction (userId: number) {
    const { me } = this
    return me.role === Role.ROOT || me.id === userId
  }

  async refreshList () {
    this.filter = {
      username: ''
    }
    this.getManagers()
  }

  async getManagers () {
    const { sortBy, page, itemsPerPage, sortDesc } = this.options
    const { username } = this.filter

    const orderBy = UsersOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0]

    this.usersLoading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: findManagers,
        variables: {
          order: [{
            by: orderBy,
            asc
          }],
          where: {
            username: username || null
          },
          paging: {
            skip: itemsPerPage * (page - 1),
            take: itemsPerPage
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }

      this.users = result.data.findManagers.users
      this.usersLoading = false
      this.totalNum = result.data.findManagers.paging.totalCount
      this.storePage()
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }

  async filterQuerySubmit () {
    await this.getManagers()
  }

  async userFormConfirm () {
    const { type } = this.managerInput

    if (type === 'new') {
      await this.newManagerConfirm()
      await this.getManagers()
    } else {
      await this.editManagerConfirm()
      await this.getManagers()
    }
  }

  async deleteDialogConfirm () {
    await this.deleteManagerConfirm()
    this.getManagers()
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
    const actionDom = this.$refs.actions as Vue & {
      clientHeight: number;
    }
    this.actionsHeight = actionDom.clientHeight
  }

  async exportHandler () {
    showNotification({
      msg: '即将开始下载...',
      type: SnackType.Info
    })
    const { sortBy, sortDesc } = this.options
    const { username } = this.filter
    const by = UsersOrderBy[sortBy[0] || 'createdAt']
    const asc = !sortDesc[0] || false
    this.usersLoading = true
    const order = { by, asc }
    const where = {
      username: username || null
    }
    try {
      const result = await this.$apollo.query({
        query: exportManagersList,
        variables: {
          order,
          where
        }
      })
      if (result.data) {
        const { fileName, contentB64 } = result.data.exportManagersList
        downloadBase64(contentB64, fileName)
      }
      this.usersLoading = false
    } catch (e) {
      const typedError = e as TypedGqlError
      showErrors(typedError.graphQLErrors)
    }
  }
}
